import { useEffect, useMemo, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { Box, Typography, Button, CircularProgress } from "@mui/material";
import { usePayment } from "../../context/PaymentContext";
import { clearCartData } from "../../../../redux/actions/restaurantDataActions";
import CheckoutHeader from "../shared/CheckoutHeader";
import { formatCurrency } from "../../../../utils/utils";
import i18next from "i18next";
import { refreshDojoPaymentDetails } from "../../../../api/app";
import {
  DojoMiniInvoiceWidget,
  DojoYourOrderWidget,
} from "../../../../components/dojo";

const OrderSuccessPageDojo = () => {
  const orderId = useParams().orderId;
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { setPaymentSuccessful } = usePayment();
  const [orderUpdationError, setOrderUpdationError] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [orderDetails, setOrderDetails] = useState(null);
  const [paymentStatus, setPaymentStatus] = useState(null);

  const themeColors = useSelector(
    (state) => state.restaurantData?.restaurantDetail?.themeColors
  );
  const { restData, tableData } = useSelector((state) => state.apiData) || {};

  const {
    orderSuccessBackBtnBgColor = null,
    orderSuccessCardFontColor = null,
  } = themeColors || {};

  const cartItems = orderDetails?.cart;
  const restaurantSlug = restData?.slug;
  const tableNumber = tableData?.name;

  // Fetch order/payment status from Dojo
  useEffect(() => {
    async function fetchOrderStatus() {
      setIsLoading(true);
      setOrderUpdationError(null);
      try {
        const res = await refreshDojoPaymentDetails(orderId);
        if (!res || !res.success) {
          throw new Error(res?.msg || "Failed to fetch payment status");
        }
        const data = res.data;
        setOrderDetails(data);
        setPaymentStatus(data.transactionStatus);
        // Save to localStorage if paid and not already present
        const existingOrders =
          JSON.parse(localStorage.getItem("pastOrders")) || [];
        const orderExists = existingOrders.some(
          (order) => order._id === data._id
        );
        if (!orderExists) {
          const newOrder = {
            ...data,
            tableNumber: tableNumber,
            cart: data?.cart,
          };
          localStorage.setItem(
            "pastOrders",
            JSON.stringify([...existingOrders, newOrder])
          );
        }
        setIsLoading(false);
      } catch (error) {
        setOrderUpdationError(error.message);
        setIsLoading(false);
      }
    }
    if (orderId) fetchOrderStatus();
    else setOrderUpdationError("Order ID not found");
    // eslint-disable-next-line
  }, [orderId, tableNumber]);

  const handleBackToRestaurantButtonClick = () => {
    setPaymentSuccessful(false);
    dispatch(clearCartData());
    if (tableNumber) {
      navigate(`/r/menu/${restaurantSlug}/table/${tableNumber}`);
    } else {
      navigate(`/r/${restaurantSlug}`);
    }
  };

  // Prepare billing list with useMemo
  const billingList = useMemo(() => [
    {
      label: i18next.t("Subtotal"),
      value: orderDetails?.totalCartAmount ?? 0,
    },
    ...(orderDetails?.charges?.chargeItems
      ?.filter((charge) => charge.isActive)
      ?.map((charge) => ({
        label: charge.name,
        value: charge.isPercentage
          ? (orderDetails.totalCartAmount * charge.value) / 100
          : charge.value,
      })) || []),
    ...(orderDetails?.discountAmount
      ? [{
        label: i18next.t("Discount"),
        value: orderDetails?.discountAmount,
      }]
      : []),
    ...(orderDetails?.couponDiscountAmount
      ? [{
        label: i18next.t("Coupon Discount"),
        value: orderDetails?.couponDiscountAmount,
      }]
      : []),
    ...(orderDetails?.tip
      ? [
        {
          label: i18next.t("Tip"),
          value: orderDetails.tip,
        },
      ]
      : []),
  ], [
    orderDetails?.charges?.chargeItems,
    orderDetails?.couponDiscountAmount,
    orderDetails?.discountAmount,
    orderDetails?.tip,
    orderDetails?.totalCartAmount,
  ]);

  if (isLoading) {
    return (
      <Box
        display="flex"
        justifyContent="center"
        alignItems="center"
        height="100vh"
      >
        <CircularProgress />
        <Typography ml={2}>Checking your payment...</Typography>
      </Box>
    );
  }


  return (
    <Box
      className="device-width-view"
      display="flex"
      flexDirection="column"
      minHeight="100vh"
      overflow="auto"
      boxSizing="border-box"
      backgroundColor="#ffffff"
      pb={10}
    >
      <Box className="device-width">
        <CheckoutHeader headerTitle="Order Overview" disableBackButton />

        <DojoMiniInvoiceWidget
          orderUpdationError={orderUpdationError}
          orderData={orderDetails}
          tableNumber={tableNumber}
          paymentStatus={paymentStatus}
        />

        <DojoYourOrderWidget orderData={orderDetails} cartItems={cartItems} />

        <Box
          p={2}
          m={1}
          sx={{
            mx: 1,
            mt: 3,
            p: 2,
            boxShadow: "0 4px 4px rgba(0, 0, 0, 0.25)",
            borderRadius: "15px",
          }}
        >
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              mb: 3,
            }}
          >
            <Typography
              sx={{
                ...styles.thankyouText,
                color: orderSuccessCardFontColor ?? "#212529",
              }}
            >
              Your Bill
            </Typography>

            <Box>
              <Typography
                sx={{
                  fontFamily: "GilroyBold",
                  fontSize: 20,
                  color: "#212529",
                }}
              >
                {formatCurrency(orderDetails?.remainingToPay || orderDetails?.totalAmount)}
              </Typography>
            </Box>
          </Box>

          <Box
            sx={{
              display: "flex",
              flexDirection: "column",
              justifyContent: "flex-start",
              alignItems: "stretch",
            }}
          >
            {billingList.map(
              (item, i) =>
                item?.value > 0 && (
                  <Typography
                    key={i}
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "space-between",
                      mb: 0.5,
                    }}
                  >
                    <span
                      style={{
                        fontFamily: "GilroyRegular",
                        fontSize: 13,
                        color: orderSuccessCardFontColor ?? "#212529",
                      }}
                    >
                      {item?.label}
                    </span>
                    <strong
                      style={{
                        fontFamily: "GilroyRegular",
                        fontSize: 13,
                        color: orderSuccessCardFontColor ?? "#212529",
                      }}
                    >
                      {item?.label === 'Coupon Discount' || item?.label === 'Discount' ? `- ${formatCurrency(item?.value)}` : formatCurrency(item?.value)}
                    </strong>
                  </Typography>
                )
            )}
            <Typography
              sx={{
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                mb: 0.5,
              }}
            >
              <span
                style={{
                  fontFamily: "GilroyRegular",
                  fontSize: 18,
                  color: orderSuccessCardFontColor ?? "#212529",
                }}
              >
                Total
              </span>
              <strong
                style={{
                  fontFamily: "GilroyRegular",
                  fontSize: 18,
                  color: orderSuccessCardFontColor ?? "#212529",
                }}
              >
                {formatCurrency(orderDetails?.remainingToPay || orderDetails?.totalAmount)}
              </strong>
            </Typography>
          </Box>
        </Box>

        <Button
          className="order-success-btn-device-width"
          variant="contained"
          color="primary"
          onClick={handleBackToRestaurantButtonClick}
          sx={{
            zIndex: 100,
            position: "fixed",
            bottom: "20px",
            backgroundColor: "#212121",
            cursor: "pointer",
            textTransform: "capitalize",
            color: "#FFF",
            fontFamily: "Inter",
            fontSize: 14,
            fontWeight: 600,
            letterSpacing: "1px",
            borderRadius: "10px",
            height: 54,
            mx: 1,
            "&:hover": {
              backgroundColor: `${orderSuccessBackBtnBgColor ?? "#212121"
                } !important`,
            },
          }}
        >
          Back to Home
        </Button>
      </Box>
    </Box>
  );
};

export default OrderSuccessPageDojo;

const styles = {
  thankyouText: {
    color: "#212529",
    fontSize: 23,
    fontFamily: "GilroyBold",
    textAlign: "center",
  },
};
